import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Trophy, Copy, ExternalLink, CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

/**
 * NudgeCentre — "Get more out of HealthCircles".
 * Renders a header (total credits + current/next badge with progress bar),
 * followed by a stack of action cards. Each in-app action shows a progress bar
 * that snaps to 100% on completion with a subtle micro-confetti; each offer
 * action shows a reveal → discount-code + copy CTA.
 */
export default function NudgeCentre() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState({});   // code -> { discount_code, external_url }
  const [celebrating, setCelebrating] = useState(null);   // code being celebrated

  const load = useCallback(async () => {
    try {
      const r = await api.get("/nudges");
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load nudges");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const currentPct = React.useMemo(() => {
    if (!data?.current_badge || !data?.next_badge) return 100;
    const lo = data.current_badge.min_credits || 0;
    const hi = data.next_badge.min_credits;
    if (hi <= lo) return 100;
    return Math.min(100, Math.max(0, Math.round(((data.totals.credits - lo) / (hi - lo)) * 100)));
  }, [data]);

  const handleReveal = async (action) => {
    try {
      const r = await api.get(`/nudges/${action.code}/reveal`);
      setRevealed((v) => ({ ...v, [action.code]: r.data }));
      if (r.data.credits_earned > 0) {
        triggerCelebrate(action.code);
        toast.success(`+${r.data.credits_earned} credits`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reveal code"); }
  };

  const triggerCelebrate = (code) => {
    setCelebrating(code);
    setTimeout(() => setCelebrating(null), 1600);
  };

  const copyCode = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("Code copied");
    } catch { toast.error("Copy failed"); }
  };

  if (loading) return <div className="py-16 flex items-center justify-center text-[#8A9992]" data-testid="nudge-loading"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="nudge-centre">
      {/* Header — credits + badge + progress toward next tier */}
      <div className="rounded-3xl bg-gradient-to-br from-[#0A4C36] via-[#0F5A40] to-[#123F30] text-[#FDFBF7] px-6 py-6 md:px-8 md:py-7 shadow-sm">
        <div className="flex items-start gap-5 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
              <Sparkles size={12} /> Growth
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <span className="text-4xl md:text-5xl font-semibold" style={{ fontFamily: "Instrument Sans" }} data-testid="nudge-total-credits">
                {data.totals.credits}
              </span>
              <span className="text-sm opacity-80">credits earned</span>
            </div>
            <div className="mt-1 text-xs opacity-80">
              {data.totals.completed}/{data.totals.available} value-creating actions completed
            </div>
          </div>
          <div className="min-w-[240px] flex-1 md:flex-none">
            {data.current_badge && (
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest opacity-70">Current tier</div>
                <div className="mt-1 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5" data-testid="nudge-current-badge">
                  <span className="text-lg">{data.current_badge.icon}</span>
                  <span className="text-sm font-semibold">{data.current_badge.name}</span>
                </div>
              </div>
            )}
            {data.next_badge && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-[11px] opacity-80">
                  <span>{data.current_badge?.icon} {data.current_badge?.name}</span>
                  <span>{data.next_badge.icon} {data.next_badge.name}</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-white/15 overflow-hidden">
                  <div className="h-full bg-[#FCE7D9] transition-all duration-700" style={{ width: `${currentPct}%` }} />
                </div>
                <div className="mt-1.5 text-[11px] opacity-80">
                  <b>{data.next_badge_delta}</b> more credits to reach <b>{data.next_badge.name}</b>.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action list */}
      <div className="space-y-3" data-testid="nudge-actions">
        {data.actions.map((a) => (
          <ActionCard
            key={a.code}
            action={a}
            revealed={revealed[a.code]}
            celebrating={celebrating === a.code}
            onOpen={() => a.cta_href && nav(a.cta_href)}
            onReveal={() => handleReveal(a)}
            onCopy={copyCode}
          />
        ))}
      </div>
    </div>
  );
}

function ActionCard({ action, revealed, celebrating, onOpen, onReveal, onCopy }) {
  const done = action.completed;
  const isOffer = action.type === "offer";
  const progress = done ? 100 : 0;

  return (
    <div
      className={`relative rounded-2xl border p-4 md:p-5 transition-all overflow-hidden ${
        done ? "border-[#0A4C36]/20 bg-[#F5FAF7]" : "border-[#EFECE5] bg-[#FDFBF7] hover:border-[#0A4C36]/30"
      }`}
      data-testid={`nudge-action-${action.code}`}
    >
      {celebrating && <ConfettiBurst />}
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {done && <CheckCircle2 size={16} className="text-[#0A4C36] flex-none" />}
            <h4 className={`text-[15px] font-semibold ${done ? "text-[#0A4C36]" : "text-[#1F2A24]"}`} style={{ fontFamily: "Instrument Sans" }}>
              {action.title}
            </h4>
            <span className="ml-auto md:ml-0 md:hidden inline-flex items-center gap-1 text-[11px] font-bold text-[#0A4C36] px-2 py-1 rounded-full bg-[#E6F0EC]" data-testid={`nudge-credits-${action.code}`}>
              +{action.credits}
            </span>
          </div>
          <p className="text-[13px] text-[#5C7568] mt-1.5 leading-relaxed">{action.description}</p>

          {/* Progress bar */}
          <div className="mt-3 h-1.5 rounded-full bg-[#EFECE5] overflow-hidden">
            <div
              className={`h-full transition-all duration-700 ${done ? "bg-[#0A4C36]" : "bg-[#B5C0BA]"}`}
              style={{ width: `${progress}%` }}
              data-testid={`nudge-progress-${action.code}`}
            />
          </div>

          {/* Revealed code panel for offers */}
          {isOffer && revealed && (
            <div className="mt-4 rounded-xl bg-[#FCE7D9] px-4 py-3 flex items-center gap-3" data-testid={`nudge-revealed-${action.code}`}>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-[#8A5A34]">Your discount code</div>
                <div className="font-mono font-semibold text-[#4A2C13] text-sm mt-0.5 truncate">{revealed.discount_code || "—"}</div>
              </div>
              <button
                onClick={() => onCopy(revealed.discount_code || "")}
                className="btn-outline-emerald !py-1.5 !px-3 text-xs"
                data-testid={`nudge-copy-${action.code}`}
              >
                <Copy size={13} /> Copy
              </button>
              {revealed.external_url && (
                <a
                  href={revealed.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-emerald !py-1.5 !px-3 text-xs"
                  data-testid={`nudge-visit-${action.code}`}
                >
                  Visit <ExternalLink size={13} />
                </a>
              )}
            </div>
          )}
        </div>

        <div className="hidden md:flex flex-col items-end gap-3 flex-none w-40">
          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-[#0A4C36] px-2.5 py-1 rounded-full bg-[#E6F0EC]" data-testid={`nudge-credits-${action.code}`}>
            <Trophy size={11} /> +{action.credits} credits
          </span>
          {done ? (
            <span className="text-[11px] text-[#0A4C36] font-semibold uppercase tracking-widest">Completed</span>
          ) : isOffer ? (
            <button onClick={onReveal} className="btn-emerald text-xs w-full justify-center" data-testid={`nudge-reveal-${action.code}`}>
              {action.cta_label || "Reveal"} <ArrowRight size={13} />
            </button>
          ) : (
            <button onClick={onOpen} className="btn-outline-emerald text-xs w-full justify-center" data-testid={`nudge-cta-${action.code}`}>
              {action.cta_label || "Open"} <ArrowRight size={13} />
            </button>
          )}
        </div>

        {/* Mobile action */}
        <div className="md:hidden">
          {done ? (
            <span className="text-[11px] text-[#0A4C36] font-semibold uppercase tracking-widest">Completed</span>
          ) : isOffer ? (
            <button onClick={onReveal} className="btn-emerald text-xs" data-testid={`nudge-reveal-m-${action.code}`}>
              {action.cta_label || "Reveal"} <ArrowRight size={13} />
            </button>
          ) : (
            <button onClick={onOpen} className="btn-outline-emerald text-xs" data-testid={`nudge-cta-m-${action.code}`}>
              {action.cta_label || "Open"} <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Tiny CSS-only confetti burst — 12 emoji particles that fall + fade. */
function ConfettiBurst() {
  const particles = React.useMemo(() => {
    const items = [];
    for (let i = 0; i < 14; i++) {
      const left = Math.random() * 100;
      const delay = Math.random() * 0.15;
      const dur = 1.2 + Math.random() * 0.4;
      const rotate = Math.random() * 360;
      const kind = ["✨", "🎉", "⭐"][i % 3];
      items.push({ left, delay, dur, rotate, kind });
    }
    return items;
  }, []);
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="nudge-confetti">
      {particles.map((p, i) => (
        <span
          key={i}
          className="absolute top-0 text-sm"
          style={{
            left: `${p.left}%`,
            animation: `nudgeConfetti ${p.dur}s ease-in ${p.delay}s forwards`,
            transform: `rotate(${p.rotate}deg)`,
            opacity: 0,
          }}
        >
          {p.kind}
        </span>
      ))}
      <style>{`
        @keyframes nudgeConfetti {
          0%   { transform: translateY(-10px) rotate(0deg); opacity: 0; }
          15%  { opacity: 1; }
          100% { transform: translateY(160px) rotate(360deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
